import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { supabase } from "@/integrations/supabase/client";
import { Reveal } from "@/hooks/useReveal";
import { SEO } from "@/components/SEO";
import { Magnetic } from "@/components/MagneticButton";
import {
  ArrowUpRight,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  FileSignature,
  User,
  Rocket,
  Mail,
  CalendarClock,
  X,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Investor role first, then other common executive roles.
const roleKeys = [
  "investor",
  "founder",
  "ceo",
  "cfo",
  "coo",
  "cto",
  "partner",
  "advisor",
  "other",
] as const;

type FormState = {
  name: string;
  email: string;
  company: string;
  role: (typeof roleKeys)[number] | "";
};

const initial: FormState = {
  name: "",
  email: "",
  company: "",
  role: "",
};

export default function Ventures() {
  const { t, lang } = useI18n();
  // Reuse NDA copy from Contact wizard (same legal text).
  const W = (t.contact as any).wizard as any;
  const VW = (t as any).ventures as any;
  const e = t.contact.errors as Record<string, string>;

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [ndaOpen, setNdaOpen] = useState(false);
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [ndaAcceptedAt, setNdaAcceptedAt] = useState<Date | null>(null);
  const [data, setData] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});

  const step1Schema = z.object({
    name: z.string().trim().min(1, e.nameRequired).min(2, e.nameTooShort).max(100),
    email: z.string().trim().email(e.emailInvalid).max(255),
    company: z.string().trim().min(1, e.companyRequired).max(120),
    role: z.enum(roleKeys, {
      errorMap: () => ({ message: VW.roleRequired ?? e.companyRequired }),
    }),
  });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setData((d) => ({ ...d, [key]: value }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validateStep = (s: 1 | 2): boolean => {
    if (s === 1) {
      const parsed = step1Schema.safeParse(data);
      if (!parsed.success) {
        const fe: Partial<Record<keyof FormState, string>> = {};
        parsed.error.issues.forEach((i) => {
          const k = i.path[0] as keyof FormState;
          if (!fe[k]) fe[k] = i.message;
        });
        setErrors((prev) => ({ ...prev, ...fe }));
        return false;
      }
      return true;
    }
    if (s === 2) return ndaAccepted;
    return true;
  };

  const wizardRef = useRef<HTMLDivElement>(null);
  const scrollToWizard = () => {
    queueMicrotask(() => {
      const el = wizardRef.current;
      if (!el) return;
      const y = el.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({ top: y, behavior: "smooth" });
    });
  };

  const goNext = () => {
    if (!validateStep(step)) return;
    if (step === 2) {
      onSubmit();
      return;
    }
    setStep((s) => (Math.min(2, s + 1) as 1 | 2));
    scrollToWizard();
  };

  const goBack = () => {
    setStep((s) => (Math.max(1, s - 1) as 1 | 2));
    scrollToWizard();
  };

  const onSubmit = async (ev?: FormEvent<HTMLFormElement>) => {
    ev?.preventDefault();
    if (!validateStep(1)) { setStep(1); return; }
    if (!ndaAccepted) { setStep(2); return; }

    setSubmitting(true);
    try {
      const roleLabel = data.role
        ? (VW.roles as Record<string, string>)[data.role] ?? data.role
        : "";
      // We reuse the existing contact-brief edge function so this lands in the
      // same operational pipeline (NDA PDF + admin/prospect emails). The
      // program context is encoded in the message body.
      await supabase.functions.invoke("submit-contact-brief", {
        body: {
          source: "ventures",
          language: lang,
          name: data.name,
          email: data.email,
          company: data.company,
          role: roleLabel,
          projectType: ["other"],
          industry: "other",
          budget: "",
          timeline: "",
          message: VW.submitMessage,
          ndaClauses: W.ndaClauses,
          ndaConsent: W.ndaConsent,
          ndaAcceptedAt: (ndaAcceptedAt ?? new Date()).toISOString(),
        },
      });
      setSubmitted(true);
      toast({ title: VW.toastTitle, description: VW.toastDesc });
    } catch (err) {
      console.error("submit-contact-brief (ventures) failed", err);
      toast({ title: VW.toastTitle, description: VW.toastDesc });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const acceptNda = () => {
    setNdaAccepted(true);
    setNdaAcceptedAt(new Date());
    setNdaOpen(false);
  };

  const acceptedDate = useMemo(() => {
    if (!ndaAcceptedAt) return "";
    return ndaAcceptedAt.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }, [ndaAcceptedAt]);

  const fillTemplate = (tpl: string, vars: Record<string, string>) =>
    tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "");

  const ndaVars = {
    company: data.company || "—",
    name: data.name || "—",
    email: data.email || "—",
    date: acceptedDate || "—",
  };

  const resetAll = () => {
    setData(initial);
    setErrors({});
    setNdaAccepted(false);
    setNdaAcceptedAt(null);
    setSubmitted(false);
    setStep(1);
  };

  return (
    <>
      <SEO title={VW.seoTitle} description={VW.seoDesc} />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,hsl(var(--foreground)/0.05),transparent_60%)]" />
          <div className="absolute inset-0 bg-grid-fine opacity-40 mask-radial-fade" />
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-10 pt-24 lg:pt-32 pb-16">
          <Reveal>
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-6">
              — {VW.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-display text-5xl lg:text-8xl font-semibold tracking-tighter leading-[0.95] max-w-5xl text-balance">
              {VW.title}
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p className="mt-8 max-w-2xl text-lg lg:text-xl text-muted-foreground leading-relaxed">
              {VW.subtitle}
            </p>
          </Reveal>
          <Reveal delay={320}>
            <Link
              to="/services"
              className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {VW.backToServices}
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WIZARD */}
      <section className="border-t hairline">
        <div className="mx-auto max-w-4xl px-6 lg:px-10 py-16 lg:py-24">
          {submitted ? (
            <Reveal>
              <div className="rounded-3xl border hairline bg-foreground/[0.012] p-8 sm:p-12 lg:p-16 text-center">
                <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border hairline bg-background">
                  <CheckCircle2 className="h-7 w-7" strokeWidth={1.5} />
                </div>
                <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tighter">
                  {VW.submittedTitle}
                </h2>
                <p className="mt-5 mx-auto max-w-xl text-base lg:text-lg text-muted-foreground leading-relaxed">
                  {(() => {
                    const parts = (VW.submittedBody as string).split("{email}");
                    return (
                      <>
                        {parts[0]}
                        <span className="text-foreground font-medium">{data.email}</span>
                        {parts[1] ?? ""}
                      </>
                    );
                  })()}
                </p>

                <div className="mt-10 grid sm:grid-cols-3 gap-4 text-left">
                  {[
                    { Icon: Mail, label: VW.successSteps[0] },
                    { Icon: CalendarClock, label: VW.successSteps[1] },
                    { Icon: Rocket, label: VW.successSteps[2] },
                  ].map(({ Icon, label }, i) => (
                    <div
                      key={i}
                      className="rounded-2xl border hairline bg-background px-5 py-5"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg border hairline mb-3">
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                      </span>
                      <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
                        0{i + 1}
                      </p>
                      <p className="text-sm text-foreground leading-snug">{label}</p>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-10 inline-flex h-11 items-center gap-2 rounded-full border hairline px-5 text-sm hover:bg-foreground/[0.04]"
                >
                  {VW.submittedAgain}
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <div
                ref={wizardRef}
                className="rounded-3xl border hairline bg-foreground/[0.012] overflow-hidden scroll-mt-24"
              >
                <ProgressHeader step={step} total={2} W={W} />

                <form
                  onSubmit={(ev) => { ev.preventDefault(); goNext(); }}
                  noValidate
                  className="px-6 sm:px-10 lg:px-12 pb-10 pt-2"
                >
                  {step === 1 && (
                    <StepShell icon={User} title={VW.step1Title} subtitle={VW.step1Sub}>
                      <div className="grid sm:grid-cols-2 gap-6">
                        <Field
                          name="name"
                          label={t.contact.formName}
                          placeholder={t.contact.placeholderName}
                          value={data.name}
                          error={errors.name}
                          onChange={(v) => set("name", v)}
                          required
                        />
                        <Field
                          name="email"
                          type="email"
                          label={t.contact.formEmail}
                          placeholder={t.contact.placeholderEmail}
                          value={data.email}
                          error={errors.email}
                          onChange={(v) => set("email", v)}
                          required
                        />
                      </div>
                      <Field
                        name="company"
                        label={t.contact.formCompany}
                        placeholder={t.contact.placeholderCompany}
                        value={data.company}
                        error={errors.company}
                        onChange={(v) => set("company", v)}
                        required
                      />
                      <Select
                        label={VW.formRole}
                        placeholder={VW.rolePlaceholder}
                        value={data.role}
                        error={errors.role as string | undefined}
                        onChange={(v) => set("role", v as FormState["role"])}
                        options={roleKeys.map((k) => ({
                          value: k,
                          label: (VW.roles as Record<string, string>)[k],
                        }))}
                        required
                      />

                      <div className="flex items-start gap-3 rounded-2xl border hairline bg-background/60 p-4 sm:p-5">
                        <ShieldCheck className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" strokeWidth={1.6} />
                        <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                          {VW.step1NdaHint}
                        </p>
                      </div>
                    </StepShell>
                  )}

                  {step === 2 && (
                    <StepShell icon={ShieldCheck} title={W.step2Title} subtitle={W.step2Sub}>
                      <button
                        type="button"
                        onClick={() => setNdaOpen(true)}
                        className={cn(
                          "group w-full text-left rounded-2xl border hairline bg-background p-6 sm:p-8 transition-all duration-300",
                          "hover:bg-foreground/[0.03] hover:border-foreground/30",
                          ndaAccepted && "border-foreground/40 bg-foreground/[0.03]",
                        )}
                      >
                        <div className="flex items-start justify-between gap-6">
                          <div className="flex items-start gap-4 min-w-0">
                            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border hairline bg-background">
                              <FileSignature className="h-5 w-5" strokeWidth={1.5} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">
                                {W.ndaCardKicker}
                              </p>
                              <p className="font-display text-xl lg:text-2xl tracking-tight truncate">
                                {fillTemplate(W.ndaCardTitle, { company: data.company || "—" })}
                              </p>
                              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                                {W.ndaCardSub}
                              </p>
                            </div>
                          </div>
                          <ArrowUpRight
                            className="h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                            strokeWidth={1.5}
                          />
                        </div>

                        {ndaAccepted && (
                          <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-500/40 bg-emerald-500/[0.06] px-4 py-3">
                            <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" strokeWidth={1.8} />
                            <div className="text-sm">
                              <p className="font-medium text-emerald-700 dark:text-emerald-300">{W.ndaAcceptedBadge}</p>
                              <p className="text-muted-foreground text-xs mt-0.5">
                                {fillTemplate(W.ndaAcceptedSub, ndaVars)}
                              </p>
                            </div>
                          </div>
                        )}
                      </button>
                    </StepShell>
                  )}

                  {/* Footer nav */}
                  <div className="mt-10 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-4 border-t hairline pt-6">
                    {step > 1 ? (
                      <button
                        type="button"
                        onClick={goBack}
                        className="inline-flex h-11 items-center gap-2 rounded-full border hairline px-5 text-sm transition-colors hover:bg-foreground/[0.04]"
                      >
                        <ArrowLeft className="h-4 w-4" />
                        {W.back}
                      </button>
                    ) : (
                      <div />
                    )}

                    <Magnetic>
                      {step === 2 && !ndaAccepted ? (
                        <TooltipProvider delayDuration={100}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <button
                                  type="button"
                                  disabled
                                  className="group inline-flex h-12 items-center gap-2 rounded-full bg-foreground px-7 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-40"
                                >
                                  {VW.submit}
                                  <ArrowUpRight className="h-4 w-4" />
                                </button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top" sideOffset={8}>
                              <p className="max-w-xs text-xs">{W.ndaRequiredTooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <button
                          type={step === 2 ? "submit" : "button"}
                          onClick={step === 2 ? undefined : goNext}
                          disabled={submitting}
                          className="group inline-flex h-12 items-center gap-2 rounded-full bg-foreground px-7 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
                        >
                          {step === 2 ? (submitting ? "…" : VW.submit) : W.next}
                          {step === 2 ? (
                            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          ) : (
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                          )}
                        </button>
                      )}
                    </Magnetic>
                  </div>
                </form>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* NDA Sheet — reuses contact wizard NDA content */}
      <Sheet open={ndaOpen} onOpenChange={setNdaOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl lg:max-w-2xl p-0 flex flex-col gap-0"
        >
          <SheetHeader className="px-6 sm:px-8 pt-8 pb-6 border-b hairline space-y-2">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              {W.ndaCardKicker}
            </p>
            <SheetTitle className="font-display text-2xl lg:text-3xl tracking-tight">
              {W.ndaSheetTitle}
            </SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground leading-relaxed">
              {W.ndaSheetDesc}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-6 text-sm leading-relaxed">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-3">
                {W.ndaPartiesTitle}
              </p>
              <ul className="space-y-2 text-foreground">
                <li>• {W.ndaPartyA}</li>
                <li>• {fillTemplate(W.ndaPartyB, ndaVars)}</li>
              </ul>
              <p className="mt-3 text-xs text-muted-foreground">
                {fillTemplate(W.ndaEffective, {
                  date:
                    acceptedDate ||
                    new Date().toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }),
                })}
              </p>
            </div>

            <div className="space-y-5">
              {(W.ndaClauses as { h: string; b: string }[]).map((c) => (
                <div key={c.h}>
                  <p className="font-medium text-foreground">{c.h}</p>
                  <p className="mt-1 text-muted-foreground">{c.b}</p>
                </div>
              ))}
            </div>

            <p className="text-xs text-muted-foreground italic border-t hairline pt-4">
              {W.ndaConsent}
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 border-t hairline px-6 sm:px-8 py-5 bg-background">
            <button
              type="button"
              onClick={() => setNdaOpen(false)}
              className="inline-flex h-11 items-center gap-2 rounded-full border hairline px-5 text-sm hover:bg-foreground/[0.04]"
            >
              <X className="h-4 w-4" />
              {W.ndaCancelBtn}
            </button>
            <Magnetic>
              <button
                type="button"
                onClick={acceptNda}
                className="group inline-flex h-12 items-center gap-2 rounded-full bg-foreground px-6 text-sm font-medium text-background hover:opacity-90"
              >
                <CheckCircle2 className="h-4 w-4" />
                {W.ndaAcceptBtn}
              </button>
            </Magnetic>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/* -------- Wizard chrome (local copies, kept lean for this page) -------- */

function ProgressHeader({ step, total, W }: { step: number; total: number; W: any }) {
  const pct = (step / total) * 100;
  return (
    <div className="px-6 sm:px-10 lg:px-12 pt-8 pb-6">
      <div className="mb-3">
        <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
          {W.stepLabel} {String(step).padStart(2, "0")} {W.of} {String(total).padStart(2, "0")}
        </span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full bg-foreground/[0.08]">
        <div
          className="h-full bg-foreground transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function StepShell({
  icon: Icon,
  title,
  subtitle,
  children,
}: {
  icon: any;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-8 pt-8">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border hairline bg-background">
          <Icon className="h-5 w-5" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <h2 className="font-display text-2xl lg:text-3xl tracking-tight">{title}</h2>
          {subtitle && (
            <p className="mt-2 text-sm lg:text-base text-muted-foreground leading-relaxed max-w-2xl">
              {subtitle}
            </p>
          )}
        </div>
      </div>
      <div className="space-y-6">{children}</div>
    </div>
  );
}

function Field({
  name,
  label,
  type = "text",
  placeholder,
  value,
  error,
  onChange,
  required,
}: {
  name: string;
  label: string;
  type?: string;
  placeholder?: string;
  value: string;
  error?: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  return (
    <div>
      <label
        htmlFor={name}
        className={cn(
          "block text-xs uppercase tracking-wider text-muted-foreground mb-3 transition-colors",
          error && "text-destructive",
        )}
      >
        {label}
        {required && " *"}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={
          name === "email" ? "email" : name === "name" ? "name" : name === "company" ? "organization" : "off"
        }
        maxLength={type === "email" ? 255 : 120}
        className={cn(
          "w-full rounded-xl border hairline bg-background px-4 py-3 text-base outline-none transition-colors placeholder:text-muted-foreground/40 focus:border-foreground",
          error && "!border-destructive focus:!border-destructive field-shake",
        )}
        aria-invalid={!!error}
      />
    </div>
  );
}

function Select({
  label,
  options,
  value,
  onChange,
  placeholder,
  required,
  error,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
}) {
  return (
    <div>
      <label
        className={cn(
          "block text-xs uppercase tracking-wider text-muted-foreground mb-3 transition-colors",
          error && "text-destructive",
        )}
      >
        {label}
        {required && " *"}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            "w-full appearance-none rounded-xl border hairline bg-background px-4 py-3 pr-10 text-base outline-none transition-colors focus:border-foreground",
            !value && "text-muted-foreground/60",
            error && "!border-destructive focus:!border-destructive field-shake",
          )}
          aria-invalid={!!error}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o.value} value={o.value} className="text-foreground">
              {o.label}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
        >
          <path d="M6 9l6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}